import React from 'react';
import { Link } from 'react-router-dom';
import GdprConsentCheckbox from '../components/GdprConsentCheckbox';
import GoogleAddressAutocomplete from '../components/GoogleAddressAutocomplete';
import QuoteRequestSuccess, { QuoteSuccessPrimaryLink } from '../components/QuoteRequestSuccess';
import { useDrivingKmForRoute } from '../hooks/useDrivingKmForRoute';
import { filterHufDigitsInput } from '../utils/hufInput';
import { isValidFlexiblePhone, PHONE_FORMAT_MESSAGE, PHONE_PLACEHOLDER, PHONE_REQUIRED_MESSAGE } from '../utils/phoneValidation';
import QuoteEstimateModal from '../components/QuoteEstimateModal';
import {
  toPositiveNumber,
  computeNetEstimateFromRates,
  formatEstimateFt,
  ESTIMATE_MIN_NET_HUF,
  pricingSnapshot,
} from '../utils/csomagStyleEstimate';
import usePricingSettings from '../hooks/usePricingSettings';
import { readFileAsBase64Payload, postQuoteInquiry, emailWarningsFromInquiryResponse } from '../utils/quoteInquiryApi';
import '../App.css';

const MAX_FILE_MB = 10;
/** Legfeljebb ennyi csatolmány sor adható hozzá (az első is opcionális). */
const MAX_ATTACHMENT_SLOTS = 10;
const ACCEPT_EXT = ['.jpg', '.jpeg', '.png', '.pdf'];

const COUNTRIES = [
  'Magyarország',
  'Ausztria',
  'Szlovákia',
  'Románia',
  'Németország',
  'Szlovénia',
  'Horvátország',
  'Egyéb (megjegyzésben)',
];

const PAYMENT_OPTIONS = [
  { value: '', label: '— Kérjük, válasszon egy opciót —' },
  { value: 'elore', label: 'Fuvar előtt (előre utalás)' },
  { value: 'utan_keszpenz', label: 'Fuvar után készpénzben' },
  { value: 'szamla', label: 'Banki átutalás / számla alapján' },
  { value: 'egyeztetes', label: 'Egyeztetés alapján' },
];

const STEPS = [
  { id: 'route', title: 'Honnan és hová' },
  { id: 'shipment', title: 'A küldemény adatai' },
  { id: 'mode', title: 'Szállítási mód és időpont' },
  { id: 'insurance', title: 'Biztosítás' },
  { id: 'contact', title: 'Ajánlatkérő adatai' },
];

function validateFile(file) {
  if (!file) return null;
  const lower = file.name.toLowerCase();
  const ok = ACCEPT_EXT.some((ext) => lower.endsWith(ext.slice(1)));
  if (!ok) return 'Csak jpg, png vagy pdf tölthető fel.';
  if (file.size > MAX_FILE_MB * 1024 * 1024) return `Max. ${MAX_FILE_MB} MB.`;
  return null;
}

const initialForm = {
  fromAddressSearch: '',
  fromCity: '',
  fromZip: '',
  fromCountry: 'Magyarország',
  fromPlaceId: '',
  toAddressSearch: '',
  toCity: '',
  toZip: '',
  toCountry: 'Magyarország',
  toPlaceId: '',
  distanceKm: '',
  shipmentDesc: '',
  weightKg: '',
  volumeM3: '',
  note: '',
  deliveryMode: 'normal',
  scheduleType: 'flexible',
  flexFrom: '',
  flexTo: '',
  fixDate: '',
  paymentWhen: '',
  currency: 'HUF',
  insurance: '',
  shipmentValue: '',
  name: '',
  phone: '',
  email: '',
  emailConfirm: '',
  privacyAccepted: false,
};

export default function KoltoztetesQuotePage() {
  const { pricingSettings } = usePricingSettings();
  const [step, setStep] = React.useState(0);
  const [form, setForm] = React.useState(initialForm);
  const [attachmentSlotIds, setAttachmentSlotIds] = React.useState(() => ['att-0']);
  const [filesBySlot, setFilesBySlot] = React.useState({});
  const [fileErrorsBySlot, setFileErrorsBySlot] = React.useState({});
  const [stepError, setStepError] = React.useState('');
  const [invalidFieldId, setInvalidFieldId] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const [submitEmailWarning, setSubmitEmailWarning] = React.useState('');
  const [submitPending, setSubmitPending] = React.useState(false);
  const [estimateOpen, setEstimateOpen] = React.useState(false);

  const formRef = React.useRef(form);
  formRef.current = form;
  const { distanceLoading, distanceError, calculateDistanceIfReady } = useDrivingKmForRoute(formRef, setForm);

  React.useEffect(() => {
    if (!estimateOpen) return;
    calculateDistanceIfReady(
      form.fromPlaceId,
      form.toPlaceId,
      String(form.fromAddressSearch || '').trim(),
      String(form.toAddressSearch || '').trim(),
    );
  }, [
    estimateOpen,
    form.fromPlaceId,
    form.toPlaceId,
    form.fromAddressSearch,
    form.toAddressSearch,
    calculateDistanceIfReady,
  ]);

  const aggWeight = toPositiveNumber(form.weightKg);
  const aggVolumeM3 = toPositiveNumber(form.volumeM3);
  const distanceKm = Math.max(0, Math.round(toPositiveNumber(form.distanceKm)));
  const { estimateNet } = computeNetEstimateFromRates(
    aggWeight,
    aggVolumeM3,
    distanceKm,
    pricingSettings.shared,
  );
  const estimatedNetForPayload =
    estimateNet > 0
      ? estimateNet
      : Number(pricingSettings.shared?.missingDistanceFallbackNetHuf || pricingSettings.shared?.minNetHuf || ESTIMATE_MIN_NET_HUF);
  const formatFt = formatEstimateFt;

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setStepError('');
  };

  const addAttachmentSlot = () => {
    setAttachmentSlotIds((ids) => {
      if (ids.length >= MAX_ATTACHMENT_SLOTS) return ids;
      return [...ids, `att-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`];
    });
    setStepError('');
  };

  const removeAttachmentSlot = (slotId) => {
    setAttachmentSlotIds((ids) => {
      if (ids.length <= 1) return ids;
      return ids.filter((id) => id !== slotId);
    });
    setFilesBySlot((f) => {
      const next = { ...f };
      delete next[slotId];
      return next;
    });
    setFileErrorsBySlot((e) => {
      const next = { ...e };
      delete next[slotId];
      return next;
    });
    setStepError('');
  };

  const setAttachmentFile = (slotId, file) => {
    if (!file) {
      setFilesBySlot((f) => {
        const next = { ...f };
        delete next[slotId];
        return next;
      });
      setFileErrorsBySlot((e) => {
        const next = { ...e };
        delete next[slotId];
        return next;
      });
      setStepError('');
      return;
    }
    const errMsg = validateFile(file);
    if (errMsg) {
      setFileErrorsBySlot((e) => ({ ...e, [slotId]: errMsg }));
      setStepError('');
      return;
    }
    setFileErrorsBySlot((e) => {
      const next = { ...e };
      delete next[slotId];
      return next;
    });
    setFilesBySlot((f) => ({ ...f, [slotId]: file }));
    setStepError('');
  };


  const scrollToInvalidField = (fieldId) => {
    if (!fieldId) return;
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus?.();
  };

  const validateStep = (s) => {
    switch (s) {
      case 0:
        if (!form.fromCity.trim()) return { message: 'Töltsd ki a felvételi címet.', fieldId: 'fromCity' };
        if (!form.fromZip.trim()) return { message: 'Töltsd ki a felvételi címet.', fieldId: 'fromZip' };
        if (!form.fromCountry) return { message: 'Töltsd ki a felvételi címet.', fieldId: 'fromCountry' };
        if (!form.toCity.trim()) return { message: 'Töltsd ki a lerakási címet.', fieldId: 'toCity' };
        if (!form.toZip.trim()) return { message: 'Töltsd ki a lerakási címet.', fieldId: 'toZip' };
        if (!form.toCountry) return { message: 'Töltsd ki a lerakási címet.', fieldId: 'toCountry' };
        return { message: '', fieldId: '' };
      case 1:
        if (!form.shipmentDesc.trim()) return { message: 'Add meg a küldemény listáját és leírását.', fieldId: '' };
        if (!form.weightKg.trim()) return { message: 'Add meg a súlyt (kg).', fieldId: '' };
        if (!form.volumeM3.trim()) return { message: 'Add meg a térfogatot (m³).', fieldId: '' };
        if (!form.note.trim()) return { message: 'Add meg a megjegyzést.', fieldId: '' };
        for (const id of attachmentSlotIds) {
          if (fileErrorsBySlot[id]) {
            return { message: 'Egy csatolmány nem felel meg a követelményeknek (max. 10 MB, jpg/png/pdf).', fieldId: '' };
          }
        }
        return { message: '', fieldId: '' };
      case 2:
        if (!form.deliveryMode) return { message: 'Válassz szállítási módot.', fieldId: '' };
        if (form.scheduleType === 'flexible') {
          if (!form.flexFrom || !form.flexTo) return { message: 'Add meg a rugalmas felvételi idősáv kezdetét és végét.', fieldId: '' };
        } else if (!form.fixDate) return { message: 'Add meg a fix felvételi napot.', fieldId: '' };
        if (!form.paymentWhen) return { message: 'Válaszd ki, mikor szeretnél fizetni.', fieldId: '' };
        if (!form.currency) return { message: 'Válassz pénznemet.', fieldId: '' };
        return { message: '', fieldId: '' };
      case 3:
        if (form.insurance !== 'igen' && form.insurance !== 'nem') return { message: 'Jelezd, hogy igényelsz-e biztosítást.', fieldId: '' };
        if (form.insurance === 'igen' && !form.shipmentValue.trim()) return { message: 'Add meg a küldemény értékét.', fieldId: '' };
        return { message: '', fieldId: '' };
      case 4:
        if (!form.name.trim()) return { message: 'Add meg a neved.', fieldId: '' };
        if (!form.phone.trim()) return { message: PHONE_REQUIRED_MESSAGE, fieldId: '' };
        if (!isValidFlexiblePhone(form.phone)) {
          return { message: PHONE_FORMAT_MESSAGE, fieldId: '' };
        }
        if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return { message: 'Érvényes e-mail címet adj meg.', fieldId: '' };
        if (form.email !== form.emailConfirm) return { message: 'Az e-mail címek nem egyeznek.', fieldId: '' };
        if (!form.privacyAccepted) return { message: 'Az adatkezelési tájékoztató elfogadása kötelező.', fieldId: '' };
        return { message: '', fieldId: '' };
      default:
        return { message: '', fieldId: '' };
    }
  };

  const next = () => {
    const result = validateStep(step);
    if (result.message) {
      setStepError(result.message);
      setInvalidFieldId(result.fieldId || '');
      setTimeout(() => scrollToInvalidField(result.fieldId), 40);
      return;
    }
    setInvalidFieldId('');
    setStep((x) => Math.min(x + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const back = () => {
    setStepError('');
    setInvalidFieldId('');
    setStep((x) => Math.max(x - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = validateStep(4);
    if (result.message) {
      setStepError(result.message);
      return;
    }
    setInvalidFieldId('');
    const filesInOrder = attachmentSlotIds.map((id) => filesBySlot?.[id]).filter(Boolean);
    let attachmentUploads = [];
    try {
      attachmentUploads = await Promise.all(filesInOrder.map((f) => readFileAsBase64Payload(f)));
    } catch (readErr) {
      setStepError(readErr?.message || 'Nem sikerült beolvasni a csatolmányokat.');
      return;
    }
    const attachmentNames = filesInOrder.map((f) => f?.name).filter(Boolean);

    const shipments = [
      {
        type: 'Költöztetés',
        packaging: form.shipmentDesc,
        content: form.note,
        weightKg: form.weightKg,
        lengthCm: '',
        widthCm: '',
        heightCm: '',
        note: '',
      },
    ];

    const payload = {
      inquiryType: 'koltoztetes',
      submittedAt: new Date().toISOString(),
      route: {
        fromAddress: form.fromAddressSearch,
        toAddress: form.toAddressSearch,
        fromCity: form.fromCity,
        fromZip: form.fromZip,
        fromCountry: form.fromCountry,
        toCity: form.toCity,
        toZip: form.toZip,
        toCountry: form.toCountry,
        distanceKm: form.distanceKm || '',
      },
      shipment: {
        type: 'Költöztetés',
        packaging: form.shipmentDesc,
        content: form.note,
        weightKg: form.weightKg,
        volumeM3: form.volumeM3,
        sizeCm: '',
        note: '',
      },
      shipments,
      schedule: {
        deliveryMode: form.deliveryMode,
        schedule: form.scheduleType === 'flexible' ? `${form.flexFrom || ''} - ${form.flexTo || ''}` : form.fixDate,
        paymentWhen: form.paymentWhen,
        currency: form.currency,
      },
      insurance: {
        enabled: form.insurance === 'igen',
        shipmentValue: form.shipmentValue || '',
      },
      contact: {
        name: form.name,
        phone: form.phone,
        email: form.email,
      },
      estimate: {
        estimatedTotal: formatFt(estimatedNetForPayload),
        estimatedTotalHuf: Math.round(estimatedNetForPayload),
      },
      pricingSnapshot: pricingSnapshot(pricingSettings),
      attachmentNames,
      attachmentUploads,
    };

    setSubmitPending(true);
    try {
      const data = await postQuoteInquiry({ payload });
      setSubmitEmailWarning(emailWarningsFromInquiryResponse(data));
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error?.name === 'AbortError') {
        setStepError('A küldés túl sokáig tartott. Kérjük, próbáld újra.');
      } else {
        setStepError(error.message || 'Nem sikerült elküldeni az árajánlatkérést.');
      }
    } finally {
      setSubmitPending(false);
    }
  };

  if (submitted) {
    return (
      <main className="qf-page">
        <div className="qf-inner">
          <QuoteRequestSuccess
            title="Köszönjük az ajánlatkérést!"
            subtitle="Az adataidat sikeresen rögzítettük. Munkatársunk hamarosan felveszi veled a kapcsolatot a megadott elérhetőségeken."
            banner={
              submitEmailWarning ? (
                <div className="qf-banner qf-banner--warn" role="status">
                  {submitEmailWarning}
                </div>
              ) : null
            }
            primary={
              <QuoteSuccessPrimaryLink to="/szolgaltatas/koltoztetes">
                Vissza a költöztetéshez
              </QuoteSuccessPrimaryLink>
            }
          />
        </div>
      </main>
    );
  }

  return (
    <main className="qf-page">
      <div className="qf-inner">
        <div className="qf-head">
          <nav className="svc-breadcrumb-trail qf-breadcrumb-trail" aria-label="Útvonal">
            <Link className="svc-breadcrumb-seg" to="/" state={{ scrollTo: 'szolgaltatas' }}>
              Szolgáltatások
            </Link>
            <span className="svc-breadcrumb-sep" aria-hidden="true">
              /
            </span>
            <Link className="svc-breadcrumb-seg" to="/szolgaltatas/koltoztetes">
              Költöztetés
            </Link>
            <span className="svc-breadcrumb-sep" aria-hidden="true">
              /
            </span>
            <span className="svc-breadcrumb-current" aria-current="page">
              Árajánlatkérés
            </span>
          </nav>
          <p className="qf-kicker">Ajánlatkérés költözés esetén</p>
          <h1 className="qf-title">Árajánlatkérés</h1>
          <p className="qf-sub">
            {step + 1}. lépés / {STEPS.length} — {STEPS[step].title}
          </p>
        </div>

        <div className="qf-progress" aria-hidden="true">
          {STEPS.map((s, i) => (
            <div
              key={s.id}
              className={`qf-progress-seg${i <= step ? ' qf-progress-seg--active' : ''}`}
            />
          ))}
        </div>

        {stepError && (
          <div className="qf-banner qf-banner--error" role="alert">
            {stepError}
          </div>
        )}

        <form className="qf-form" onSubmit={step === STEPS.length - 1 ? handleSubmit : (e) => e.preventDefault()}>
          {step === 0 && (
            <div className="qf-step">
              <fieldset className="qf-fieldset">
                <legend>HONNAN</legend>
                <div className="qf-grid">
                  <GoogleAddressAutocomplete
                    id="fromAddressSearch"
                    value={form.fromAddressSearch}
                    onResolved={(addr) => {
                      const nextFromPlaceId = addr.placeId || '';
                      const snap = formRef.current;
                      setForm((prev) => ({
                        ...prev,
                        fromAddressSearch: addr.formatted !== undefined ? addr.formatted : prev.fromAddressSearch,
                        fromCity: addr.city || '',
                        fromZip: addr.zip || '',
                        fromCountry: addr.country || '',
                        fromPlaceId: nextFromPlaceId,
                        distanceKm: '',
                      }));
                      setStepError('');
                      calculateDistanceIfReady(
                        nextFromPlaceId,
                        snap.toPlaceId,
                        addr.formatted || '',
                        snap.toAddressSearch || '',
                      );
                    }}
                  />
                  <div className="field qf-field">
                    <label htmlFor="fromCity">Város *</label>
                    <input
                      id="fromCity"
                      className={invalidFieldId === 'fromCity' ? 'qf-invalid' : ''}
                      value={form.fromCity}
                      readOnly
                      required
                    />
                  </div>
                  <div className="field qf-field">
                    <label htmlFor="fromZip">Irányítószám *</label>
                    <input
                      id="fromZip"
                      className={invalidFieldId === 'fromZip' ? 'qf-invalid' : ''}
                      value={form.fromZip}
                      readOnly
                      required
                    />
                  </div>
                  <div className="field qf-field qf-field--full">
                    <label htmlFor="fromCountry">Ország *</label>
                    <select
                      id="fromCountry"
                      className={invalidFieldId === 'fromCountry' ? 'qf-invalid' : ''}
                      value={form.fromCountry}
                      disabled
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </fieldset>

              <fieldset className="qf-fieldset">
                <legend>HOVÁ</legend>
                <div className="qf-grid">
                  <GoogleAddressAutocomplete
                    id="toAddressSearch"
                    value={form.toAddressSearch}
                    onResolved={(addr) => {
                      const nextToPlaceId = addr.placeId || '';
                      const snap = formRef.current;
                      setForm((prev) => ({
                        ...prev,
                        toAddressSearch: addr.formatted !== undefined ? addr.formatted : prev.toAddressSearch,
                        toCity: addr.city || '',
                        toZip: addr.zip || '',
                        toCountry: addr.country || '',
                        toPlaceId: nextToPlaceId,
                        distanceKm: '',
                      }));
                      setStepError('');
                      calculateDistanceIfReady(
                        snap.fromPlaceId,
                        nextToPlaceId,
                        snap.fromAddressSearch || '',
                        addr.formatted || '',
                      );
                    }}
                  />
                  {distanceLoading && (
                    <small className="qf-help qf-field--full">Távolság számítása folyamatban…</small>
                  )}
                  {!distanceLoading && !distanceError && Number(form.distanceKm) > 0 && (
                    <small className="qf-help qf-field--full">
                      Számított vezetési távolság: {form.distanceKm} km
                    </small>
                  )}
                  {!!distanceError && (
                    <small className="qf-help qf-help--warn qf-field--full">{distanceError}</small>
                  )}
                  <div className="field qf-field">
                    <label htmlFor="toCity">Város *</label>
                    <input
                      id="toCity"
                      className={invalidFieldId === 'toCity' ? 'qf-invalid' : ''}
                      value={form.toCity}
                      readOnly
                      required
                    />
                  </div>
                  <div className="field qf-field">
                    <label htmlFor="toZip">Irányítószám *</label>
                    <input
                      id="toZip"
                      className={invalidFieldId === 'toZip' ? 'qf-invalid' : ''}
                      value={form.toZip}
                      readOnly
                      required
                    />
                  </div>
                  <div className="field qf-field qf-field--full">
                    <label htmlFor="toCountry">Ország *</label>
                    <select
                      id="toCountry"
                      className={invalidFieldId === 'toCountry' ? 'qf-invalid' : ''}
                      value={form.toCountry}
                      disabled
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </fieldset>
            </div>
          )}

          {step === 1 && (
            <div className="qf-step">
              <h2 className="qf-step-title">A KÜLDEMÉNY ADATAI</h2>
              <div className="field qf-field">
                <label htmlFor="shipmentDesc">Küldemény listája, és részletes leírása *</label>
                <textarea
                  id="shipmentDesc"
                  rows={5}
                  value={form.shipmentDesc}
                  onChange={(e) => update('shipmentDesc', e.target.value)}
                  placeholder="Tételek felsorolása, méretek, különleges igények…"
                />
              </div>
              <div className="qf-grid">
                <div className="field qf-field">
                  <label htmlFor="weightKg">∑ súly (kg) *</label>
                  <input
                    id="weightKg"
                    inputMode="decimal"
                    value={form.weightKg}
                    onChange={(e) => update('weightKg', e.target.value)}
                  />
                </div>
                <div className="field qf-field">
                  <label htmlFor="volumeM3">∑ térfogat (m³) *</label>
                  <input
                    id="volumeM3"
                    inputMode="decimal"
                    value={form.volumeM3}
                    onChange={(e) => update('volumeM3', e.target.value)}
                  />
                </div>
              </div>
              <div className="field qf-field">
                <label htmlFor="note">Megjegyzés, egyéb fontos információ *</label>
                <textarea
                  id="note"
                  rows={3}
                  value={form.note}
                  onChange={(e) => update('note', e.target.value)}
                />
              </div>

              <div className="qf-files">
                <p className="qf-files-hint">
                  Kép vagy dokumentum feltöltése <strong>opcionális</strong>. Formátum: jpg, png, pdf;
                  max. {MAX_FILE_MB} MB / fájl. Legfeljebb {MAX_ATTACHMENT_SLOTS} fájl adható hozzá.
                </p>
                {attachmentSlotIds.map((slotId, index) => (
                  <div className="field qf-field qf-file-row" key={slotId}>
                    <label htmlFor={slotId}>Csatolmány {index + 1} (nem kötelező)</label>
                    <div className="qf-file-row-actions">
                      <input
                        id={slotId}
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => setAttachmentFile(slotId, e.target.files?.[0] ?? null)}
                      />
                      {attachmentSlotIds.length > 1 && (
                        <button
                          type="button"
                          className="qf-file-remove"
                          onClick={() => removeAttachmentSlot(slotId)}
                        >
                          Sor törlése
                        </button>
                      )}
                    </div>
                    {filesBySlot[slotId] && (
                      <span className="qf-file-name">{filesBySlot[slotId].name}</span>
                    )}
                    {fileErrorsBySlot[slotId] && (
                      <span className="qf-file-err">{fileErrorsBySlot[slotId]}</span>
                    )}
                  </div>
                ))}
                {attachmentSlotIds.length < MAX_ATTACHMENT_SLOTS && (
                  <button type="button" className="secondary-btn qf-add-file" onClick={addAttachmentSlot}>
                    + Még egy fájl
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="qf-step">
              <div className="field qf-field">
                <label htmlFor="deliveryMode">Szállítási mód *</label>
                <select
                  id="deliveryMode"
                  value={form.deliveryMode}
                  onChange={(e) => update('deliveryMode', e.target.value)}
                >
                  <option value="normal">
                    Normál szállítás — Szállítás az első zárt ajtóig/kapuig, a sofőrnek segítségre van
                    szüksége a nehéz és terjedelmes tárgyakhoz.
                  </option>
                  <option value="inhome">
                    Rakodási segítség (Kérlek vedd figyelembe hogy a sofőr egyedül közlekedik)
                  </option>
                </select>
              </div>

              <h2 className="qf-step-title qf-step-title--spaced">SZÁLLÍTÁSI IDŐ ÉS FIZETÉS</h2>

              <div className="qf-schedule">
                <div className="field qf-field">
                  <label htmlFor="scheduleType">Felvétel típusa *</label>
                  <select
                    id="scheduleType"
                    value={form.scheduleType}
                    onChange={(e) => update('scheduleType', e.target.value)}
                  >
                    <option value="flexible">Rugalmas (felvétel)</option>
                    <option value="fixed">Fix nap (felvétel)</option>
                  </select>
                </div>
                {form.scheduleType === 'flexible' && (
                  <div className="qf-date-row">
                    <div className="field qf-field">
                      <label htmlFor="flexFrom">-tól</label>
                      <input
                        id="flexFrom"
                        type="date"
                        value={form.flexFrom}
                        onChange={(e) => update('flexFrom', e.target.value)}
                      />
                    </div>
                    <div className="field qf-field">
                      <label htmlFor="flexTo">-ig</label>
                      <input
                        id="flexTo"
                        type="date"
                        value={form.flexTo}
                        onChange={(e) => update('flexTo', e.target.value)}
                      />
                    </div>
                  </div>
                )}
                {form.scheduleType === 'fixed' && (
                  <div className="field qf-field">
                    <label htmlFor="fixDate">Dátum</label>
                    <input
                      id="fixDate"
                      type="date"
                      value={form.fixDate}
                      onChange={(e) => update('fixDate', e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="field qf-field">
                <label htmlFor="paymentWhen">Mikor szeretnél fizetni? *</label>
                <select
                  id="paymentWhen"
                  value={form.paymentWhen}
                  onChange={(e) => update('paymentWhen', e.target.value)}
                >
                  {PAYMENT_OPTIONS.map((o) => (
                    <option key={o.value || 'empty'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field qf-field">
                <label htmlFor="currency">Pénznem *</label>
                <select id="currency" value={form.currency} onChange={(e) => update('currency', e.target.value)}>
                  <option value="HUF">HUF</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="qf-step">
              <h2 className="qf-step-title">BIZTOSÍTÁS</h2>
              <fieldset className="qf-fieldset qf-fieldset--plain">
                <legend>Igényel biztosítást *</legend>
                <div className="qf-inline-radios">
                  <label className="qf-radio-inline">
                    <input
                      type="radio"
                      name="insurance"
                      value="igen"
                      checked={form.insurance === 'igen'}
                      onChange={() => update('insurance', 'igen')}
                    />
                    igen
                  </label>
                  <label className="qf-radio-inline">
                    <input
                      type="radio"
                      name="insurance"
                      value="nem"
                      checked={form.insurance === 'nem'}
                      onChange={() => update('insurance', 'nem')}
                    />
                    nem
                  </label>
                </div>
              </fieldset>

              {form.insurance === 'igen' && (
                <div className="field qf-field">
                  <label htmlFor="shipmentValue">Küldemény értéke (igen válasz esetén) *</label>
                  <input
                    id="shipmentValue"
                    inputMode="numeric"
                    autoComplete="off"
                    value={form.shipmentValue}
                    onChange={(e) => update('shipmentValue', filterHufDigitsInput(e.target.value))}
                    placeholder="pl. 500000 (csak számjegyek)"
                  />
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="qf-step">
              <h2 className="qf-step-title">AJÁNLATKÉRŐ ADATAI</h2>
              <div className="field qf-field">
                <label htmlFor="name">Név *</label>
                <input id="name" value={form.name} onChange={(e) => update('name', e.target.value)} />
              </div>
              <div className="field qf-field">
                <label htmlFor="phone">Telefonszám *</label>
                <input
                  id="phone"
                  type="tel"
                  placeholder={PHONE_PLACEHOLDER}
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                />
              </div>
              <div className="field qf-field">
                <label htmlFor="email">E-mail cím *</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                />
              </div>
              <div className="field qf-field">
                <label htmlFor="emailConfirm">E-mail cím megerősítése *</label>
                <input
                  id="emailConfirm"
                  type="email"
                  autoComplete="email"
                  value={form.emailConfirm}
                  onChange={(e) => update('emailConfirm', e.target.value)}
                />
              </div>

              <GdprConsentCheckbox
                id="koltoztetes-gdpr"
                checked={form.privacyAccepted}
                onChange={(v) => update('privacyAccepted', v)}
              />
            </div>
          )}

          <div className="qf-actions">
            {step > 0 && (
              <button type="button" className="secondary-btn qf-btn-back" onClick={back}>
                Vissza
              </button>
            )}
            {step < STEPS.length - 1 && (
              <button type="button" className="primary-btn qf-btn-next" onClick={next}>
                Tovább
              </button>
            )}
            {step === STEPS.length - 1 && (
              <>
                <button type="button" className="secondary-btn qf-btn-preview" onClick={() => setEstimateOpen(true)}>
                  Előzetes irányár megtekintése
                </button>
                <button type="submit" className="primary-btn qf-btn-submit" disabled={submitPending}>
                  {submitPending ? 'Küldés...' : 'Ajánlatkérés küldése'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
      <QuoteEstimateModal
        open={estimateOpen}
        onClose={() => setEstimateOpen(false)}
        distanceLoading={distanceLoading}
        distanceError={distanceError}
        distanceKm={distanceKm}
        weightKg={aggWeight}
        lengthCm={0}
        widthCm={0}
        heightCm={0}
        volumeM3={aggVolumeM3}
        volumeMode="aggregated"
        estimateNet={estimateNet}
      />
    </main>
  );
}
